import { Stack, TextField, Typography } from '@mui/material';

export const UNPINNING_NOTE_MAX_LENGTH = 200;

type Props = {
  value?: string;
  onChange: (value: string) => void;
  /** Singular by default; callers name the flow, e.g. "Swap note". */
  label?: string;
  disabled?: boolean;
  placeholder?: string;
};

/**
 * Optional free-form note captured alongside the unpinning reason on the
 * Remove / Swap / Relocate flows. Never required, so it must not gate submission.
 *
 * The input is multiline without a fixed row count: it starts one row tall and
 * grows as the text wraps, rather than always reserving several empty rows.
 */
export const UnpinningNoteField: React.FC<Props> = ({
  value,
  onChange,
  label = 'Note',
  disabled = false,
  placeholder = 'Add any additional note',
}) => {
  // maxLength only limits typing; paste, IME composition and autofill can all
  // exceed it, so the cap is enforced here rather than trusted to the browser.
  const safeValue = (value ?? '').slice(0, UNPINNING_NOTE_MAX_LENGTH);

  return (
    <Stack width="100%" gap={1}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
      >
        <Typography variant="body1" fontWeight={600}>
          {label} (optional)
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {safeValue.length}/{UNPINNING_NOTE_MAX_LENGTH}
        </Typography>
      </Stack>
      <TextField
        fullWidth
        multiline
        disabled={disabled}
        placeholder={placeholder}
        value={safeValue}
        onChange={(event) =>
          onChange(event.target.value.slice(0, UNPINNING_NOTE_MAX_LENGTH))
        }
        inputProps={{ maxLength: UNPINNING_NOTE_MAX_LENGTH }}
        sx={{
          '& .MuiFilledInput-root.MuiInputBase-multiline': {
            paddingTop: '12px',
            paddingBottom: '12px',
          },
        }}
      />
    </Stack>
  );
};
